/* eslint-disable import/no-cycle */
import RightChevron from 'aquilla/icons/actions/RightChevron';
import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Cross from 'aquilla/icons/misc/Cross';
import Image from '@base/FutureImage';
import Typography from 'aquilla/core/atoms/Typography';
import ReferralSuggestions from '@cont/Checkout/ReferralSuggestions';
import Drawer from '@base/Drawer/Drawer';
import { useCallback, useEffect, useState } from 'react';
import useReferralCodeSuggestions from '@cont/Checkout/useReferralCodeSuggestions';
import useFetch from '@hooks/useFetch';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { getGoalDurationMetaInfo } from '@cont/Checkout/apiHelper';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import usePricings from '@cont/Checkout/usePricings';
import { segment } from '@actions/analytics';
import { CHECKOUT_PROCEED_TO_PAYMENT_SELECTED } from '@cont/Checkout/constants/segment';
import { plusProceedPaymentEventAnalytics } from '@cont/Checkout/utils';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import useIconicProComboBundle from '@api/hooks/goal/useIconicProComboBundle';
import {
  buildIconicProComboBundle,
  buildPrimarySubscriptionFromPlan,
  isIconicProSubscription
} from '@cont/Checkout/bundleAddOnsHelper';
import isEmptyObject from '@utils/isEmptyObject';
import { NORMALFLOWSTEPS } from './NormalPlanSelection';
import PlanDetailCard from '../BaseComponents/PlanDetailCard';
import {
  defaultSelectedPricing,
  discountedPricingIfAvailable
} from '../planSelectionHelper';

const NormalPlanSelectionPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

export const ReferralBannerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_3B};
  padding: ${Spacings.SPACING_3B};
  border-radius: ${Spacings.SPACING_2B};
  background: linear-gradient(272.37deg, #ffd5a3 -4.26%, #fff9eb 111.68%);
`;

const HeaderText = styled(Typography)`
  width: 290px;
`;

const BottomWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_7B};
`;

export const ReferralRightWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const PlanDetailsCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

export const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
`;

export const StyledDrawer = styled(Drawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    padding: 0;
  }
`;

export const ApplyButton = styled.div`
  width: 71px;
  height: ${Spacings.SPACING_7B};
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 50px;
  justify-content: center;
  background-color: var(--color-i-white);
  border: 1px solid var(--color-i-orange-24);
`;

export const ApplyText = styled(Typography)`
  font-weight: ${Fonts.HEAVY};
`;

interface DurationInfoType {
  durationInfo: Record<string, any>;
}

const isReferralCodeApplicable = (plan) => {
  return (
    plan &&
    plan.referral_details &&
    ((plan.referral_details.discount_type === 1 &&
      plan.referral_details.discount_value > 0) ||
      (plan.referral_details.discount_type === 2 &&
        plan.referral_details.discount_percent > 0))
  );
};

export const updateUrlWithReferral = (
  goalSlug,
  goalUID,
  referral_code,
  referralValid
) => {
  const referralCode = referral_code ? encodeURIComponent(referral_code) : '';
  const queryParams = new URLSearchParams(window.location.search);
  queryParams.delete('referral_code');
  if (referral_code && referralValid) {
    queryParams.set('referral_code', referralCode);
  }
  const finalQueryParams = queryParams.toString()
    ? `?${queryParams.toString()}`
    : '';
  pushRoute(
    `/goal/[goalSlug]/[goalUID]/subscribe${finalQueryParams}`,
    `/goal/${goalSlug}/${goalUID}/subscribe${finalQueryParams}`
  );
};

const NormalPlanSelectionFlowPage = ({
  setFlowSteps,
  setSelectedPlan,
  selectedPlan,
  preFilteredPlusSubscriptionLists,
  referralCode,
  purchaseDetails,
  setReferralCode,
  referralError,
  validateReferralInProgress,
  hasIconicSubscription,
  setBundleAddOnData,
  goalDetails,
  setPreviousStep,
  referralValid,
  referralAllLoading,
  discountedPricings,
  setCurrentEventProps,
  showBackButton
}) => {
  const { name = '' } = goalDetails ?? {};
  const router = useRouter();
  const { asPath = '', query } = router ?? {};

  const { goalUID = '', goalSlug = '', referral_code = null } = query ?? {};

  const { pricings } = usePricings({ goalUID });
  const { goal = {} } = pricings ?? {};
  const { isLoading: isMeDataLoading, isValidating: isMeDataValidating } =
    useMyInfo();
  const me = useMyInfo(getMe);

  const { tax_breakdown = [] } = selectedPlan ?? {};

  const [goalDurationData, setGoalDurationData] =
    useState<Array<Record<string, any> | undefined>>();

  useEffect(() => {
    (async () => {
      const data = await getGoalDurationMetaInfo(goalUID);
      if (!data?.error) setGoalDurationData(data);
    })();
  }, [goalUID]);

  useEffect(() => {
    if (referral_code) setReferralCode(referral_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral_code]);

  const [isApplyCouponDrawerOpen, setIsApplyCouponDrawerOpen] =
    useState<boolean>(false);
  const [planSelection, setPlanSelection] = useState<number>(0);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (!isApplyCouponDrawerOpen)
      setSelectedPlan(preFilteredPlusSubscriptionLists[0]);
  }, [
    isApplyCouponDrawerOpen,
    preFilteredPlusSubscriptionLists,
    setSelectedPlan
  ]);

  const { referralCodes = [] } = useReferralCodeSuggestions(
    preFilteredPlusSubscriptionLists.length > 0 &&
      preFilteredPlusSubscriptionLists[0]?.uid
  );

  const firstReferralCode =
    referralCodes.length > 0
      ? referralCodes?.find((code) => code?.isActive === true)
      : {};

  useFirstRenderSkip(() => {
    if (!referralAllLoading || !referralCode)
      updateUrlWithReferral(goalSlug, goalUID, referralCode, referralValid);
  }, [goalSlug, goalUID, referralCode, referralValid, referralAllLoading]);

  const closeCouponsDrawer = useCallback(() => {
    setIsApplyCouponDrawerOpen(false);
  }, []);

  const isSelectedIconicPro = isIconicProSubscription(selectedPlan);

  const { data: bundleAddOnsInfo, isLoading: fetchingBundleAddOns } = useFetch(
    hasSubmitted && !hasIconicSubscription && !isSelectedIconicPro
      ? `v2/subscriptions/bundle_add_ons/details/?subscription_uid=${
          selectedPlan?.uid
        }${referralCode ? `&referral=${encodeURIComponent(referralCode)}` : ''}`
      : null,
    { useCamelCase: false },
    { shouldRetryOnError: false }
  );

  const {
    topologyDetails: iconicProCmsTopologyDetails,
    hasComboTopologies: hasIconicProCmsComboTopologies,
    isLoading: fetchingIconicProComboCms
  } = useIconicProComboBundle(goalUID, isSelectedIconicPro && hasSubmitted);

  const getReferralCodeTitle = () => {
    if (referralValid && referralCode) return `Applied ${referralCode}`;
    if (firstReferralCode?.code) return `Use ${firstReferralCode?.code}`;
    return 'Have a referral code?';
  };

  const isReferralAdded = referralValid && !!referralCode;

  const eventProps = useCheckoutEventProps(
    goalUID,
    discountedPricingIfAvailable(
      selectedPlan?.uid,
      isReferralAdded,
      discountedPricings
    ) || defaultSelectedPricing(selectedPlan?.uid, pricings),
    false
  );

  const subscription_price =
    tax_breakdown.length > 0
      ? tax_breakdown.find((tax) => {
          return tax?.label === 'Total';
        })
      : null;

  const evtPayload = {
    ...eventProps,
    referral_code: referralCode || 'NA',
    is_referral_successful: Boolean(referralCode) && !referralError,
    subscription_price: Number(subscription_price?.amount)
  };

  const isMeDataProcessing = isMeDataLoading && isMeDataValidating;

  useEffect(() => {
    if (!isMeDataProcessing)
      plusProceedPaymentEventAnalytics({
        ...evtPayload,
        referral_code,
        ...me,
        userId: me?.uid,
        ...goal,
        user_auth: !isEmptyObject(me)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeDataProcessing]);

  useEffect(() => {
    if (hasSubmitted) {
      setCurrentEventProps(evtPayload);
      if (hasIconicSubscription) {
        setHasSubmitted(false);
        setPreviousStep(NORMALFLOWSTEPS.PLANSELECTION);
        setFlowSteps(NORMALFLOWSTEPS.ADDICONIC);
      } else if (
        isSelectedIconicPro ? !fetchingIconicProComboCms : !fetchingBundleAddOns
      ) {
        const shouldShowCombo = isSelectedIconicPro
          ? hasIconicProCmsComboTopologies
          : Boolean(bundleAddOnsInfo?.bundle_add_ons?.length);

        if (shouldShowCombo) {
          setHasSubmitted(false);
          if (isSelectedIconicPro) {
            const primarySubscription = buildPrimarySubscriptionFromPlan(
              selectedPlan,
              pricings?.goal
            );
            const cmsCombo = buildIconicProComboBundle({
              topologyDetails: iconicProCmsTopologyDetails,
              primarySubscription
            });
            setBundleAddOnData({
              bundleAddOnsInfo: {
                bundle_add_ons: cmsCombo ? [cmsCombo] : [],
                primary_subscription: primarySubscription
              },
              skuDetails: selectedPlan
            });
          } else {
            setBundleAddOnData({
              bundleAddOnsInfo,
              skuDetails: selectedPlan
            });
          }
          setPreviousStep(NORMALFLOWSTEPS.PLANSELECTION);
          setFlowSteps(NORMALFLOWSTEPS.ADDON);
        } else {
          segment.track(CHECKOUT_PROCEED_TO_PAYMENT_SELECTED, evtPayload);
          pushRoute({
            pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
            query: {
              ...query,
              subscriptionUID: selectedPlan.uid
            }
          });
          setHasSubmitted(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bundleAddOnsInfo,
    bundleAddOnsInfo?.bundle_add_ons?.length,
    fetchingBundleAddOns,
    fetchingIconicProComboCms,
    hasIconicProCmsComboTopologies,
    isSelectedIconicPro,
    hasIconicSubscription,
    hasSubmitted,
    query,
    selectedPlan,
    setBundleAddOnData,
    setFlowSteps,
    setPreviousStep
  ]);

  const handlePlanSelection = () => {
    setHasSubmitted(true);
  };

  const { referral: { discountPercent = '' } = {} } =
    purchaseDetails?.subscription ?? {};

  return (
    <NormalPlanSelectionPageWrapper>
      {showBackButton && (
        <HeaderText variant="h2">
          Select duration for your {name} prep
        </HeaderText>
      )}
      <BottomWrapper>
        <ReferralBannerWrapper>
          <Image
            src="newPlanSelection/referralTicket.svg"
            width={40}
            height={40}
            alt="Referral Ticket"
          />
          <ReferralRightWrapper>
            <InfoWrapper>
              <Typography variant="h6">{getReferralCodeTitle()}</Typography>
              {(firstReferralCode?.discountPercent || discountPercent) && (
                <Typography
                  variant="p3"
                  color={discountPercent && 'var(--color-i-brown-14)'}
                >
                  {referralValid && discountPercent
                    ? `Saving ${discountPercent}%`
                    : `Get ${firstReferralCode?.discountPercent}% off on all subscriptions`}
                </Typography>
              )}
            </InfoWrapper>
            {referralValid && referralCode ? (
              <Cross
                width={24}
                height={24}
                onClick={() => {
                  setReferralCode('');
                }}
              />
            ) : (
              <ApplyButton
                onClick={() => {
                  setIsApplyCouponDrawerOpen(true);
                }}
              >
                <ApplyText variant="p3">Apply</ApplyText>
                <RightChevron width={18} height={18} />
              </ApplyButton>
            )}
          </ReferralRightWrapper>
        </ReferralBannerWrapper>
        <PlanDetailsCardWrapper>
          {preFilteredPlusSubscriptionLists?.map((subscription, index) => {
            const goalDurationInfo = goalDurationData
              ? goalDurationData?.find((info) => {
                  return (
                    +info?.duration === subscription?.duration &&
                    info?.goalUid === goalUID
                  );
                })
              : [];
            return (
              <PlanDetailCard
                key={subscription.uid}
                subscription={subscription}
                isRecommended={
                  subscription.recommendation_info &&
                  subscription.recommendation_info !== null
                }
                index={index}
                planSelection={planSelection}
                onClick={() => {
                  setPlanSelection(index);
                  setSelectedPlan(subscription);
                }}
                onSubmit={handlePlanSelection}
                isReferralCodeApplicable={
                  isReferralCodeApplicable(subscription) && !!referralCode
                }
                isReferralAdded={isReferralAdded}
                goalDurationInfo={goalDurationInfo as DurationInfoType}
                discountedPricings={discountedPricings}
              />
            );
          })}
        </PlanDetailsCardWrapper>
      </BottomWrapper>
      <StyledDrawer
        drawerState={isApplyCouponDrawerOpen}
        hideCloseButton
        onClose={closeCouponsDrawer}
      >
        <ReferralSuggestions
          onClose={closeCouponsDrawer}
          onApply={(referralCode) => {
            setReferralCode(referralCode);
          }}
          referralCode={referralCode}
          validateReferralInProgress={validateReferralInProgress}
          isReferralValid={referralCode && !referralError}
          referralMessage={referralError}
          subscriptionUid={selectedPlan?.uid}
        />
      </StyledDrawer>
    </NormalPlanSelectionPageWrapper>
  );
};

export default NormalPlanSelectionFlowPage;
